/**
 * Agent Authentication Service
 * Extends AuthenticationService to support AI agent-specific authentication
 */

import { AuthenticationService, OAuthTokens, APIKeyCredentials } from './AuthenticationService';

export interface WebhookSignature {
  secret: string;
  algorithm: 'sha256' | 'sha1' | 'md5';
  headerName: string;
  signaturePrefix?: string;
}

export interface AgentCredentials {
  agentId: string;
  type: 'api_key' | 'oauth' | 'webhook_signature';
  credentials: APIKeyCredentials | OAuthTokens | WebhookSignature;
  permissions: AgentPermission[];
  rateLimit: RateLimitConfig;
  metadata: {
    name: string;
    description: string;
    owner: string;
    createdAt: Date;
    lastUsed?: Date;
  };
}

export interface AgentPermission {
  resource: 'campaigns' | 'leads' | 'emails' | 'segments' | 'analytics';
  actions: string[]; // ['create', 'read', 'update', 'delete']
  constraints?: {
    maxRecords?: number;
    allowedFields?: string[];
    timeRange?: { hours: number };
  };
}

export interface RateLimitConfig {
  requests: {
    perMinute: number;
    perHour: number;
    perDay: number;
  };
  quotas: {
    monthly: {
      requests: number;
      campaigns: number;
      leads: number;
      emails: number;
    };
  };
  burstAllowance: number;
  backoffPolicy: {
    enabled: boolean;
    multiplier: number;
    maxDelay: number;
  };
}

export interface AgentRegistration {
  id: string;
  name: string;
  description: string;
  owner: string;
  status: 'active' | 'suspended' | 'revoked';
  credentials: AgentCredentials;
  permissions: AgentPermission[];
  rateLimit: RateLimitConfig;
  usage: {
    totalRequests: number;
    successfulRequests: number;
    failedRequests: number;
    lastRequest?: Date;
    quotaUsed: number;
    quotaLimit: number;
    quotaResetDate: Date;
  };
  audit: {
    createdAt: Date;
    createdBy: string;
    lastModified: Date;
    lastModifiedBy: string;
  };
}

export class AgentAuthenticationService extends AuthenticationService {
  private registeredAgents: Map<string, AgentRegistration> = new Map();
  private apiKeys: Map<string, string> = new Map(); // apiKey -> agentId mapping

  constructor() {
    super();
    this.initializeMockAgents();
  }

  /**
   * Initialize mock agents for testing
   */
  private initializeMockAgents(): void {
    const now = new Date();
    
    // Mock agent 1
    const mockAgent1: AgentRegistration = {
      id: 'agent_test_1',
      name: 'Test Agent 1',
      description: 'Mock agent for testing',
      owner: 'test_user',
      status: 'active',
      credentials: {
        agentId: 'agent_test_1',
        type: 'api_key',
        credentials: {
          apiKey: 'agent_agent_test_1_token'
        } as APIKeyCredentials,
        permissions: [],
        rateLimit: {
          requests: {
            perMinute: 100,
            perHour: 1000,
            perDay: 10000
          },
          quotas: {
            monthly: {
              requests: 100000,
              campaigns: 1000,
              leads: 50000,
              emails: 100000
            }
          },
          burstAllowance: 10,
          backoffPolicy: {
            enabled: true,
            multiplier: 2,
            maxDelay: 60000
          }
        },
        metadata: {
          name: 'Test Agent 1',
          description: 'Mock agent for testing',
          owner: 'test_user',
          createdAt: now
        }
      },
      permissions: [
        {
          resource: 'campaigns',
          actions: ['create', 'read', 'update', 'delete']
        },
        {
          resource: 'leads',
          actions: ['create', 'read', 'update', 'delete']
        },
        {
          resource: 'emails',
          actions: ['send', 'read']
        },
        {
          resource: 'segments',
          actions: ['create', 'read', 'update', 'delete']
        },
        {
          resource: 'analytics',
          actions: ['read']
        }
      ],
      rateLimit: {
        requests: {
          perMinute: 100,
          perHour: 1000,
          perDay: 10000
        },
        quotas: {
          monthly: {
            requests: 100000,
            campaigns: 1000,
            leads: 50000,
            emails: 100000
          }
        },
        burstAllowance: 10,
        backoffPolicy: {
          enabled: true,
          multiplier: 2,
          maxDelay: 60000
        }
      },
      usage: {
        totalRequests: 0,
        successfulRequests: 0,
        failedRequests: 0,
        quotaUsed: 0,
        quotaLimit: 100000,
        quotaResetDate: new Date(now.getFullYear(), now.getMonth() + 1, 1)
      },
      audit: {
        createdAt: now,
        createdBy: 'test_user',
        lastModified: now,
        lastModifiedBy: 'test_user'
      }
    };

    // Mock agent for integration testing
    const mockAgent2: AgentRegistration = {
      id: 'test_agent_integration',
      name: 'Test Agent Integration',
      description: 'Mock agent for integration testing',
      owner: 'integration_test',
      status: 'active',
      credentials: {
        agentId: 'test_agent_integration',
        type: 'api_key',
        credentials: {
          apiKey: 'test_token'
        } as APIKeyCredentials,
        permissions: [],
        rateLimit: {
          requests: {
            perMinute: 100,
            perHour: 1000,
            perDay: 10000
          },
          quotas: {
            monthly: {
              requests: 100000,
              campaigns: 1000,
              leads: 50000,
              emails: 100000
            }
          },
          burstAllowance: 10,
          backoffPolicy: {
            enabled: true,
            multiplier: 2,
            maxDelay: 60000
          }
        },
        metadata: {
          name: 'Test Agent Integration',
          description: 'Mock agent for integration testing',
          owner: 'integration_test',
          createdAt: now
        }
      },
      permissions: [
        {
          resource: 'campaigns',
          actions: ['create', 'read', 'update', 'delete']
        },
        {
          resource: 'leads',
          actions: ['create', 'read', 'update', 'delete']
        },
        {
          resource: 'emails',
          actions: ['send', 'read']
        },
        {
          resource: 'segments',
          actions: ['create', 'read', 'update', 'delete']
        },
        {
          resource: 'analytics',
          actions: ['read']
        }
      ],
      rateLimit: {
        requests: {
          perMinute: 100,
          perHour: 1000,
          perDay: 10000
        },
        quotas: {
          monthly: {
            requests: 100000,
            campaigns: 1000,
            leads: 50000,
            emails: 100000
          }
        },
        burstAllowance: 10,
        backoffPolicy: {
          enabled: true,
          multiplier: 2,
          maxDelay: 60000
        }
      },
      usage: {
        totalRequests: 0,
        successfulRequests: 0,
        failedRequests: 0,
        quotaUsed: 0,
        quotaLimit: 100000,
        quotaResetDate: new Date(now.getFullYear(), now.getMonth() + 1, 1)
      },
      audit: {
        createdAt: now,
        createdBy: 'integration_test',
        lastModified: now,
        lastModifiedBy: 'integration_test'
      }
    };

    // Register mock agents
    this.registeredAgents.set('agent_test_1', mockAgent1);
    this.registeredAgents.set('test_agent_integration', mockAgent2);
    
    // Set up API key mappings
    this.apiKeys.set('agent_agent_test_1_token', 'agent_test_1');
    this.apiKeys.set('test_token', 'test_agent_integration');
  }

  /**
   * Register a new AI agent
   */
  async registerAgent(registration: Omit<AgentRegistration, 'id' | 'audit' | 'usage'>): Promise<string> {
    const agentId = this.generateAgentId();
    const now = new Date();

    const agentRegistration: AgentRegistration = {
      ...registration,
      id: agentId,
      usage: {
        totalRequests: 0,
        successfulRequests: 0,
        failedRequests: 0,
        quotaUsed: 0,
        quotaLimit: registration.rateLimit.quotas.monthly.requests,
        quotaResetDate: new Date(now.getFullYear(), now.getMonth() + 1, 1) // First day of next month
      },
      audit: {
        createdAt: now,
        createdBy: registration.owner,
        lastModified: now,
        lastModifiedBy: registration.owner
      }
    };

    this.registeredAgents.set(agentId, agentRegistration);

    // If using API key authentication, store the mapping
    if (registration.credentials.type === 'api_key') {
      const apiKeyCredentials = registration.credentials.credentials as APIKeyCredentials;
      this.apiKeys.set(apiKeyCredentials.apiKey, agentId);
    }

    return agentId;
  }

  /**
   * Generate API key for an agent
   */
  generateAPIKey(): string {
    const prefix = 'ak_'; // Agent Key prefix
    const randomBytes = new Uint8Array(32);
    crypto.getRandomValues(randomBytes);
    const key = Array.from(randomBytes, byte => byte.toString(16).padStart(2, '0')).join('');
    return prefix + key;
  }

  /**
   * Generate OAuth client credentials for an agent
   */
  generateOAuthCredentials(): { clientId: string; clientSecret: string } {
    const clientId = 'agent_' + this.generateRandomString(16);
    const clientSecret = this.generateRandomString(32);
    return { clientId, clientSecret };
  }

  /**
   * Generate webhook signature secret
   */
  generateWebhookSecret(): string {
    return 'whsec_' + this.generateRandomString(32);
  }

  /**
   * Validate webhook signature
   */
  validateWebhookSignature(
    payload: string,
    signature: string,
    secret: string,
    algorithm: 'sha256' | 'sha1' | 'md5' = 'sha256'
  ): boolean {
    try {
      const expectedSignature = this.generateWebhookSignature(payload, secret, algorithm);
      return this.secureCompare(signature, expectedSignature);
    } catch (error) {
      console.error('Webhook signature validation error:', error);
      return false;
    }
  }

  /**
   * Generate webhook signature
   */
  private generateWebhookSignature(
    payload: string,
    secret: string,
    algorithm: 'sha256' | 'sha1' | 'md5'
  ): string {
    const encoder = new TextEncoder();
    const _keyData = encoder.encode(secret);
    const _messageData = encoder.encode(payload);

    // For browser compatibility, we'll use a simplified approach
    // In a real implementation, you'd use crypto.subtle.sign with HMAC
    return `${algorithm}=${this.simpleHmac(payload, secret, algorithm)}`;
  }

  /**
   * Simple HMAC implementation for demo purposes
   * In production, use crypto.subtle.sign with HMAC
   */
  private simpleHmac(message: string, key: string, _algorithm: string): string {
    // This is a simplified implementation for demo purposes
    // In production, use proper HMAC with crypto.subtle
    const combined = key + message;
    let hash = 0;
    for (let i = 0; i < combined.length; i++) {
      const char = combined.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(16);
  }

  /**
   * Secure string comparison to prevent timing attacks
   */
  private secureCompare(a: string, b: string): boolean {
    if (a.length !== b.length) {
      return false;
    }

    let result = 0;
    for (let i = 0; i < a.length; i++) {
      result |= a.charCodeAt(i) ^ b.charCodeAt(i);
    }

    return result === 0;
  }

  /**
   * Get agent by API key
   */
  getAgentByAPIKey(apiKey: string): AgentRegistration | null {
    const agentId = this.apiKeys.get(apiKey);
    if (!agentId) {
      return null;
    }
    return this.registeredAgents.get(agentId) || null;
  }

  /**
   * Get agent by ID
   */
  getAgent(agentId: string): AgentRegistration | null {
    return this.registeredAgents.get(agentId) || null;
  }

  /**
   * Update agent registration
   */
  async updateAgent(agentId: string, updates: Partial<AgentRegistration>): Promise<boolean> {
    const agent = this.registeredAgents.get(agentId);
    if (!agent) {
      return false;
    }

    const updatedAgent: AgentRegistration = {
      ...agent,
      ...updates,
      id: agentId, // Ensure ID cannot be changed
      audit: {
        ...agent.audit,
        lastModified: new Date(),
        lastModifiedBy: updates.owner || agent.owner
      }
    };

    this.registeredAgents.set(agentId, updatedAgent);
    return true;
  }

  /**
   * Revoke agent access
   */
  async revokeAgent(agentId: string): Promise<boolean> {
    const agent = this.registeredAgents.get(agentId);
    if (!agent) {
      return false;
    }

    agent.status = 'revoked';
    agent.audit.lastModified = new Date();

    // Remove API key mapping if exists
    if (agent.credentials.type === 'api_key') {
      const apiKeyCredentials = agent.credentials.credentials as APIKeyCredentials;
      this.apiKeys.delete(apiKeyCredentials.apiKey);
    }

    return true;
  }

  /**
   * List all registered agents
   */
  listAgents(): AgentRegistration[] {
    return Array.from(this.registeredAgents.values());
  }

  /**
   * Update agent usage statistics
   */
  updateAgentUsage(agentId: string, success: boolean): void {
    const agent = this.registeredAgents.get(agentId);
    if (!agent) {
      return;
    }

    agent.usage.totalRequests++;
    agent.usage.lastRequest = new Date();
    agent.credentials.metadata.lastUsed = new Date();

    if (success) {
      agent.usage.successfulRequests++;
    } else {
      agent.usage.failedRequests++;
    }

    agent.usage.quotaUsed++;
  }

  /**
   * Check if agent has exceeded rate limits
   */
  checkRateLimit(agentId: string): { allowed: boolean; resetTime?: Date; remaining?: number } {
    const agent = this.registeredAgents.get(agentId);
    if (!agent) {
      return { allowed: false };
    }

    // Check monthly quota
    if (agent.usage.quotaUsed >= agent.usage.quotaLimit) {
      return {
        allowed: false,
        resetTime: agent.usage.quotaResetDate,
        remaining: 0
      };
    }

    // In a real implementation, you'd also check per-minute, per-hour, per-day limits
    // This would require storing request timestamps and implementing sliding window logic

    return {
      allowed: true,
      remaining: agent.usage.quotaLimit - agent.usage.quotaUsed,
      resetTime: agent.usage.quotaResetDate
    };
  }

  /**
   * Generate random string
   */
  private generateRandomString(length: number): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    const randomArray = new Uint8Array(length);
    crypto.getRandomValues(randomArray);
    
    for (let i = 0; i < length; i++) {
      result += chars[randomArray[i] % chars.length];
    }
    
    return result;
  }

  /**
   * Generate unique agent ID
   */
  private generateAgentId(): string {
    return 'agent_' + Date.now().toString(36) + '_' + this.generateRandomString(8);
  }

  /**
   * Authenticate an agent using provided credentials
   */
  async authenticateAgent(agentId: string, headers: Record<string, string>): Promise<{ success: boolean; error?: string }> {
    const agent = this.registeredAgents.get(agentId);
    if (!agent) {
      return { success: false, error: 'Agent not found' };
    }

    if (agent.status !== 'active') {
      return { success: false, error: `Agent status is ${agent.status}` };
    }

    // Check authentication based on credential type
    switch (agent.credentials.type) {
      case 'api_key':
        return this.authenticateAPIKey(agent, headers);
      case 'oauth':
        return this.authenticateOAuth(agent, headers);
      case 'webhook_signature':
        return this.authenticateWebhookSignature(agent, headers);
      default:
        return { success: false, error: 'Unknown authentication type' };
    }
  }

  /**
   * Authenticate using API key
   */
  private async authenticateAPIKey(agent: AgentRegistration, headers: Record<string, string>): Promise<{ success: boolean; error?: string }> {
    const authHeader = headers['authorization'] || headers['Authorization'];
    if (!authHeader) {
      return { success: false, error: 'Missing Authorization header' };
    }

    const [scheme, token] = authHeader.split(' ');
    if (scheme !== 'Bearer') {
      return { success: false, error: 'Invalid authorization scheme' };
    }

    const credentials = agent.credentials.credentials as APIKeyCredentials;
    if (token !== credentials.apiKey) {
      return { success: false, error: 'Invalid API key' };
    }

    return { success: true };
  }

  /**
   * Authenticate using OAuth
   */
  private async authenticateOAuth(agent: AgentRegistration, headers: Record<string, string>): Promise<{ success: boolean; error?: string }> {
    const authHeader = headers['authorization'] || headers['Authorization'];
    if (!authHeader) {
      return { success: false, error: 'Missing Authorization header' };
    }

    const [scheme, token] = authHeader.split(' ');
    if (scheme !== 'Bearer') {
      return { success: false, error: 'Invalid authorization scheme' };
    }

    // In a real implementation, you would validate the OAuth token
    // For now, we'll just check if it's not empty
    if (!token) {
      return { success: false, error: 'Invalid OAuth token' };
    }

    return { success: true };
  }

  /**
   * Authenticate using webhook signature
   */
  private async authenticateWebhookSignature(agent: AgentRegistration, headers: Record<string, string>): Promise<{ success: boolean; error?: string }> {
    const credentials = agent.credentials.credentials as WebhookSignature;
    const signature = headers[credentials.headerName.toLowerCase()] || headers[credentials.headerName];
    
    if (!signature) {
      return { success: false, error: `Missing ${credentials.headerName} header` };
    }

    // In a real implementation, you would validate the webhook signature against the request body
    // For now, we'll just check if the signature is present
    return { success: true };
  }

  /**
   * Reset monthly quotas (should be called by a scheduled job)
   */
  resetMonthlyQuotas(): void {
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);

    Array.from(this.registeredAgents.values()).forEach(agent => {
      if (agent.usage.quotaResetDate <= now) {
        agent.usage.quotaUsed = 0;
        agent.usage.quotaResetDate = nextMonth;
      }
    });
  }
}